import { Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, InputLabel, MenuItem, OutlinedInput, Select, Stack, Typography } from '@mui/material'
import { FunctionComponent, useEffect, useState } from 'react'
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { IPostRule } from '../../../../shared/services/rule/rule.model';
import { DialogSaveButtons } from '../../../presentation-components/miscellaneous-components/dialog-save-buttons';
import { IGroupWithCategory } from '../../../../shared/services/group/group.model';
import { sortGroupsByGhgCategory, formatGroupLabel } from '../../../../shared/constants/ghg-category';
import GroupService from '../../../../shared/services/group/group.service';
import { TooltipButton } from '../../../presentation-components/miscellaneous-components/tooltip-button';
import { EmissionsFilterLogic } from '../../../presentation-components/emissons-components/overview/emissions-filter/emissions-filter-logic';
import ConversionSourceService from '../../../../shared/services/conversion-source/conversion-source.service';
import { IConversionSource } from '../../../../shared/services/conversion-source/conversion-source.model';

export const QuickRuleDialog: FunctionComponent<{
  open: boolean,
  closeHandler: () => void,
  isEdit: boolean,
  isSubmitting: boolean,
  onSubmit: (values: IPostRule, restForm: () => void, isEdit: boolean) => void,
  errorCode?: number,
  errorMessage?: string,
  conversionData: {
    categoryName: string;
    categoryId: string;
    detailLevelOne: string;
    detailLevelTwo: string;
    detailLevelThree: string;
    detailLevelFour: string;
    detailLevelFive: string;
    measurementUnitName: string;
    conversionSourceId: string;
  };
}> = ({
  open,
  closeHandler,
  isEdit,
  onSubmit,
  isSubmitting,
  errorCode,
  errorMessage,
  conversionData,
}) => {
    //Services
    const groupService = new GroupService()
    const conversionSourceService = new ConversionSourceService();
    //State
    const [conversionSourceId, setConversionSourceId] = useState<string>()
    const [groups, setGroups] = useState<IGroupWithCategory[]>()
    const [groupId, setGroupId] = useState<string>()
    const [categoryId, setCategoryId] = useState<string>()
    const [conversionSources, setConversionSources] = useState<IConversionSource[]>([]);
    /** The group lookup has come back, so an empty list means "none" rather than "not yet". */
    const [groupsChecked, setGroupsChecked] = useState(false);
    //Formik
    const ruleSchema = Yup.object().shape({
      value: Yup.string().required('You must provide a value'),
      name: Yup.string().required('You must provide a name'),
      type: Yup.string().required('You must provide a type'),
      conversionSourceId: Yup.string().required('You must select a conversion source'),
      groupName: Yup.string().required('You must provide a group'),
      categoryName: Yup.string().required('You must provide a category'),
      detailLevelOne: Yup.string().required('You must provide additional information'),
      detailLevelTwo: Yup.string().required('You must provide additional information'),
      detailLevelThree: Yup.string(),
      detailLevelFour: Yup.string().nullable(),
      detailLevelFive: Yup.string().nullable(),
      measurementUnitName: Yup.string().required('You must provide a measurement unit'),
    })
    const formik = useFormik<IPostRule>({
      initialValues: {
        value: '',
        name: '',
        type: 'activity-based emission factor',
        conversionSourceId: null,
        groupName: '',
        returnId: '',
        categoryName: '',
        detailLevelOne: '',
        detailLevelTwo: '',
        detailLevelThree: '',
        detailLevelFour: '',
        detailLevelFive: '',
        measurementUnitName: '',
        conversionId: '',
      },
      validationSchema: ruleSchema,
      onSubmit: (values) => {
        onSubmit(values, formik.resetForm, isEdit)
      }
    })
    //Use Effects
    useEffect(() => {
      conversionSourceService.getAll(true, 'conversion')
        .then(response => {
          setConversionSources(response)
        })
        .catch(err => console.log(err))

      console.log(conversionData)
    }, [])



    useEffect(() => {
      if (conversionData) {
        formik.setFieldValue('conversionSourceId', conversionData.conversionSourceId);
        formik.setFieldValue('categoryName', conversionData.categoryName);
        formik.setFieldValue('detailLevelOne', conversionData.detailLevelOne);
        formik.setFieldValue('detailLevelTwo', conversionData.detailLevelTwo);
        formik.setFieldValue('detailLevelThree', conversionData.detailLevelThree);
        formik.setFieldValue('detailLevelFour', conversionData.detailLevelFour);
        formik.setFieldValue('detailLevelFive', conversionData.detailLevelFive);
        formik.setFieldValue('measurementUnitName', conversionData.measurementUnitName);

        setConversionSourceId(conversionData.conversionSourceId);
        setCategoryId(conversionData.categoryId);

        groupService.getGroupsByCategory(conversionData.categoryId)
          .then(groupsForCategory => {
            if (groupsForCategory && groupsForCategory.length > 0) {
              groupService.getGroupsWithCategories(true)
                .then(allGroupsWithCategories => {
                  const availableGroups = allGroupsWithCategories.filter(group =>
                    groupsForCategory.some(availableGroup => availableGroup.id === group.id)
                  );
                  setGroups(availableGroups.length > 0 ? availableGroups : undefined);
                  if (availableGroups.length === 1) {
                    const selectedGroup = availableGroups[0];
                    formik.setFieldValue('groupName', selectedGroup.title);
                    setGroupId(selectedGroup.id);
                  }
                })
                .finally(() => setGroupsChecked(true));
            } else {
              setGroupsChecked(true);
            }
          })
          .catch(err => {
            console.log(err)
            setGroupsChecked(true);
          });
      }
    }, [conversionData]);

    const cancelHandler = () => {
      formik.resetForm();
      setGroupId('');
      setCategoryId('');
      closeHandler();
    };

    // The source, type, target category and factor are all fixed by the row this quick rule is
    // created from, so they render as plain text lines rather than disabled inputs.
    const selectedSourceName = conversionSources
      .find(source => source.id === conversionData?.conversionSourceId)?.source;
    const seededFactorText = conversionData
      ? [
        [
          conversionData.detailLevelOne,
          conversionData.detailLevelTwo,
          conversionData.detailLevelThree,
          conversionData.detailLevelFour,
          conversionData.detailLevelFive,
        ].filter(Boolean).join(' \u00b7 '),
        conversionData.measurementUnitName ? `(${conversionData.measurementUnitName})` : '',
      ].filter(Boolean).join(' ')
      : '';

    return (
      <Dialog open={open} onClose={closeHandler} maxWidth='xs'>
        <Box sx={{ p: 4 }} component='form' onSubmit={formik.handleSubmit}>
          <DialogTitle sx={{ pt: 0, pl: 0, pr: 0 }}>
            {!isEdit && 'Add Rule'}
            {isEdit && 'Edit Rule'}
          </DialogTitle>
          <DialogContent sx={{ pt: 0, pl: 0, pr: 0 }}>
            <FormControl fullWidth sx={{ mt: 0.5 }}>
              <InputLabel>Name</InputLabel>
              <OutlinedInput id='name'
                name='name'
                label='Name'
                fullWidth
                value={formik.values.name || ''}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                sx={{
                  color: 'text.primary', mr: 2
                }}></OutlinedInput>
              {formik.touched.name && formik.errors.name &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.name ? <div>{formik.errors.name as string}</div> : null}
                </FormHelperText>
              }
              {errorMessage && errorCode === 1 &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {errorMessage}
                </FormHelperText>
              }
            </FormControl>
            {/* A quick rule is always an activity-based factor and the control was disabled with
                a single option, so it states the type rather than offering it. */}
            <Typography variant='body2' sx={{ mt: 2 }}>
              <Typography component='span' variant='body2' sx={{ color: 'text.secondary' }}>Type: </Typography>
              Activity-Based Conversion Factor
            </Typography>
            {formik.touched.type && formik.errors.type &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.type}</FormHelperText>
            }
            {errorMessage && errorCode === 3 &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                {errorMessage}
              </FormHelperText>
            }
            {formik.values.type === 'activity-based emission factor' && selectedSourceName &&
              <Typography variant='body2' sx={{ mt: 2 }}>
                <Typography component='span' variant='body2' sx={{ color: 'text.secondary' }}>Conversion Factor Source: </Typography>
                {selectedSourceName}
              </Typography>}
            {errorMessage && errorCode === 6 &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                {errorMessage}
              </FormHelperText>
            }
            <Stack direction='row' alignItems={'center'} sx={{ mt: 2, mb: 0.5 }}>
              <Typography variant='subtitle1' >Condition</Typography>
              {<TooltipButton
                text={'The value of the condition is the term that must match in the imported data to apply the rule'}
                placement='top'
              />}
            </Stack>
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
              When the imported Emission Category is:
            </Typography>
            <FormControl fullWidth sx={{ mt: 0.5 }}>
              <InputLabel>Value</InputLabel>
              <OutlinedInput id='value'
                name='value'
                label='Value'
                fullWidth
                value={formik.values.value || ''}
                onChange={formik.handleChange}
                error={formik.touched.value && Boolean(formik.errors.value)}
                sx={{
                  color: 'text.primary', mr: 2
                }}></OutlinedInput>
              {formik.touched.value && formik.errors.value &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.value ? <div>{formik.errors.value as string}</div> : null}
                </FormHelperText>
              }
              {errorMessage && errorCode === 2 &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {errorMessage}
                </FormHelperText>
              }
            </FormControl>
            <Stack direction='row' alignItems={'center'} sx={{ mt: 2, mb: 0.5 }}>
              <Typography variant='subtitle1' >Action</Typography>
              {<TooltipButton
                text={'The classification that will be assigned when the condition is matched'}
                placement='top'
              />}
            </Stack>
            <Typography variant='body2' sx={{ mb: 1 }}>
              <Typography component='span' variant='body2' sx={{ color: 'text.secondary' }}>Set Emission Category as: </Typography>
              {conversionData.categoryName}
            </Typography>
            {errorMessage && errorCode === 5 &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                {errorMessage}
              </FormHelperText>
            }
            {formik.values.type === 'activity-based emission factor' && <>
              {groups &&
                (<FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='groupName'>Group</InputLabel>
                  <Select
                    id='groupName' label='Group' name='groupName'
                    value={formik.values.groupName || ''}
                    onChange={formik.handleChange}
                    error={formik.touched.groupName && Boolean(formik.errors.groupName)}
                    sx={{ color: 'text.primary', }}>
                    {sortGroupsByGhgCategory(groups).map((group, index) =>
                      <MenuItem key={index} value={group.title}>{formatGroupLabel(group)}</MenuItem>
                    )}
                  </Select>
                  {formik.touched.groupName && formik.errors.groupName &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.groupName}</FormHelperText>
                  }
                  {errorMessage && errorCode === 4 &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {errorMessage}
                    </FormHelperText>
                  }
                </FormControl>)
              }
              {/* Was a disabled empty dropdown, which looked like a control and was not one.
                  It also hid a dead end: a category mapped to no group can never satisfy the
                  required groupName, so the rule could never save and nothing said why. */}
              {!groups && !groupsChecked &&
                <Typography variant='body2' sx={{ mt: 2, color: 'text.secondary' }}>
                  Finding the groups this category can belong to.
                </Typography>
              }
              {!groups && groupsChecked &&
                <Typography variant='body2' sx={{ mt: 2, color: 'text.secondary' }}>
                  This category is not assigned to any group, so a rule cannot be
                  created for it yet. Add it to a group in your company settings first.
                </Typography>
              }
              {/* The factor is fixed by the row this rule was created from. The seeding effect
                  above already writes every field the rule needs (the cascade only ever set
                  returnId, which the server reads for vehicle rules alone), so this states the
                  factor instead of rendering five disabled dropdowns that restate it. */}
              {conversionData && seededFactorText &&
                <Typography variant='body2' sx={{ mt: 2 }}>
                  <Typography component='span' variant='body2' sx={{ color: 'text.secondary' }}>Emission factor: </Typography>
                  {seededFactorText}
                </Typography>}
              {!conversionData && (categoryId && conversionSourceId) &&
                <EmissionsFilterLogic
                  categoryId={categoryId}
                  groupId={groupId || ''}
                  defaultConversionSourceId={conversionSourceId}
                  formik={formik}
                  isEdit={isEdit}
                  direction='column' />}
            </>}
          </DialogContent>
          <DialogActions sx={{ p: 0 }}>
            <DialogSaveButtons
              cancelHandler={cancelHandler}
              submitHandler={formik.submitForm}
              isSubmitting={isSubmitting}
            />
          </DialogActions>
        </Box>
      </Dialog >
    )
  }